import { Component, computed, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { OrderService, PathService, DraftService, AppStateService, UserStateService, GlobalsService, PermissionsService, ProductsService } from '../../../core/services';
import { PriceService } from '../../../core/services/price.service';
import { DraftDuplicationService } from '../../../core/services/draft-duplication.service';
import { PaymentService } from '../../../core/services/payment.service';
import { ServerFile } from '../../../core/models';
import { DraftData, DraftInfo, DraftAlbumConfig, DraftProductConfig } from '../../../core/models/product.model';
import { PaymentType, isAppliedVoucher } from '../../../core/models/order.model';
import { BankTransferConfig, BoletoConfig, MultibancoConfig } from '../../../core/models/payment.model';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { GroupSelectionDialogComponent } from '../group-selection-dialog/group-selection-dialog.component';
import { OrderItemRepairMenuComponent } from '../../support/order-item-repair-menu/order-item-repair-menu.component';
import { BankTransferDetailsComponent } from '../../../shared/components/bank-transfer-details/bank-transfer-details.component';
import { MultibancoDetailsComponent } from '../../../shared/components/multibanco-details/multibanco-details.component';
import { BoletoDetailsComponent } from '../../../shared/components/boleto-details/boleto-details.component';
import { PaypalGatewayComponent } from '../../../shared/components/payment-processing-dialog/gateways/paypal-gateway.component';
import { BraintreeDialogComponent, BraintreeDialogResult } from '../../../shared/components/payment-processing-dialog/gateways/braintree-dialog.component';

export interface OrderDetailsDialogData {
  order: any;
  ordersList?: any[];    // Full list of orders for navigation
  currentIndex?: number; // Current order index in the list
  /**
   * One-shot handoff from checkout: the payment-type id to initiate via
   * processPayment as soon as the (still-pending) order loads. The cart
   * does NOT call processPayment itself — failing there looked like a
   * failed checkout and let the user place the order again.
   */
  initiatePaymentTypeId?: number | null;
  /**
   * Optional callback invoked when the user navigates between orders via the
   * prev/next arrows. Lets the host (OrdersComponent) mirror the visible
   * order into the URL bar.
   */
  onOrderChanged?: (orderId: number) => void;
}

export interface OrderDetailsDialogResult {
  action?: 'canceled' | 'duplicated';
  orderId?: number;
  draftInfo?: DraftInfo;
}

@Component({
  selector: 'app-order-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslateModule,
    OrderItemRepairMenuComponent,
    BankTransferDetailsComponent,
    MultibancoDetailsComponent,
    BoletoDetailsComponent,
    PaypalGatewayComponent
  ],
  templateUrl: './order-details-dialog.component.html',
  styleUrls: ['./order-details-dialog.component.scss']
})
export class OrderDetailsDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<OrderDetailsDialogComponent>);
  private dialog = inject(MatDialog);
  public data: OrderDetailsDialogData = inject(MAT_DIALOG_DATA);
  private orderService = inject(OrderService);
  private productsService = inject(ProductsService);
  private pathService = inject(PathService);
  private draftService = inject(DraftService);
  private priceService = inject(PriceService);
  private draftDuplication = inject(DraftDuplicationService);
  private paymentService = inject(PaymentService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private appState = inject(AppStateService);
  private userState = inject(UserStateService);
  private globals = inject(GlobalsService);

  // Hide price values when the user's showPrice flag is 0 (display only).
  pricesVisible = this.userState.pricesVisible;

  // Paying requires the CART module (customer-users guide §4.2) — an
  // ORDERS-only sub-user (e.g. Finance) can view the order but every payment
  // surface is hidden and the config prefetches are skipped (they'd 403).
  readonly perms = inject(PermissionsService);

  // PayPal Smart Button is embedded directly in the payment-box for an
  // awaiting-payment PayPal order (no stacked payment dialog).
  paypalError = signal(false);

  // Show the embedded PayPal button when this is an awaiting-payment PayPal
  // order (status 1 PENDING or 2 AWAITING_PAYMENT). Once confirmed (>=3) it
  // hides and the payment-box shows the confirmed state instead.
  showPaypalButton = computed(() => {
    if (!this.perms.canCart()) return false;
    const d = this.orderDetails();
    if (!d) return false;
    const status = Number(d.status);
    return Number(d.payment?.type) === PaymentType.PAYPAL && (status === 1 || status === 2);
  });

  // Internal order id for an embedded gateway (prefers the loaded details).
  embeddedOrderId = computed(() => {
    const d = this.orderDetails();
    return Number(d?.orderId ?? d?.id ?? this.data.order?.orderId ?? this.data.order?.id) || 0;
  });

  // ISO currency code of the order, so the PayPal SDK boots in the order's currency.
  paypalCurrencyCode = computed(() => this.priceService.codeForCurrencyId(this.orderDetails()?.currencyId));

  onPaypalCompleted(): void {
    // Capture succeeded — reload so the payment-box reflects the real (server-
    // resolved) status: confirmed (3) hides the button, pending (2) keeps it.
    this.paypalError.set(false);
    this.loadOrderDetails();
  }

  onPaypalFailed(error: { message: string; status?: number }): void {
    console.error('[OrderDetailsDialog] PayPal error:', error.message);
    // A 400 (often after "Tentar novamente") means the order is no longer
    // payable — usually because it was already captured. Reload to confirm:
    // if it's now paid (status >= 3) the button hides and the confirmed state
    // shows; otherwise the error/retry surfaces again.
    if (error.status === 400) {
      this.paypalError.set(false);
      this.loadOrderDetails();
      return;
    }
    this.paypalError.set(true);
  }

  retryPaypal(): void {
    // Re-render the gateway (it re-inits config/SDK on mount).
    this.paypalError.set(false);
  }

  // Braintree: a "Pay by card" button in the payment-box opens the Drop-in in
  // a dialog (the card form is too large to embed inline). Shown for an
  // awaiting-payment Braintree order; hidden once confirmed (>= 3).
  showBraintreeButton = computed(() => {
    if (!this.perms.canCart()) return false;
    const d = this.orderDetails();
    if (!d) return false;
    const status = Number(d.status);
    return Number(d.payment?.type) === PaymentType.BRAINTREE && (status === 1 || status === 2);
  });

  openBraintreeDialog(): void {
    const ref = this.dialog.open(BraintreeDialogComponent, {
      data: { orderId: this.embeddedOrderId(), threeDSecure: this.buildBraintreeThreeDSecure() },
      width: '460px',
      panelClass: 'braintree-dialog-panel'
    });
    ref.afterClosed().subscribe((result: BraintreeDialogResult | undefined) => {
      // paid (sale submitted) or reload (already-paid 400) → refresh the order.
      if (result?.paid || result?.reload) {
        this.loadOrderDetails();
      }
    });
  }

  /** 3D Secure params for the Braintree Drop-in. The amount is required;
   *  email + billing address improve frictionless verification (issuer needs
   *  them to skip the challenge). Missing fields are simply omitted.
   *
   *  The 3DS v2 lookup validates these fields strictly (422 otherwise):
   *  postal code is alphanumeric only ("4630-203" → "Billing postal code
   *  format is invalid"), phone is digits only, names/addresses ASCII. */
  private buildBraintreeThreeDSecure(): Record<string, any> {
    const d: any = this.orderDetails();
    const addr: any = d?.billingAddress || d?.shipmentAddress || {};
    return {
      amount: String(d?.totalValue ?? 0),
      email: this.userState.user()?.email || undefined,
      billingAddress: {
        givenName: this.threeDSecureAscii(addr.name, 50),
        phoneNumber: this.threeDSecureDigits(addr.phone),
        streetAddress: this.threeDSecureAscii(addr.address, 50),
        extendedAddress: this.threeDSecureAscii(addr.door, 50),
        locality: this.threeDSecureAscii(addr.local, 50),
        region: addr.city?.ref || undefined,
        postalCode: this.threeDSecureAlphanumeric(addr.postalCode, 10),
        countryCodeAlpha2: addr.country?.iso || undefined
      }
    };
  }

  private threeDSecureAlphanumeric(value: unknown, maxLength: number): string | undefined {
    const cleaned = String(value ?? '').replace(/[^a-zA-Z0-9]/g, '').slice(0, maxLength);
    return cleaned || undefined;
  }

  private threeDSecureDigits(value: unknown): string | undefined {
    const cleaned = String(value ?? '').replace(/\D/g, '');
    return cleaned || undefined;
  }

  /** Strips diacritics (ã→a) and any remaining non-ASCII, then trims to length. */
  private threeDSecureAscii(value: unknown, maxLength: number): string | undefined {
    const cleaned = String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\x20-\x7E]/g, '')
      .trim()
      .slice(0, maxLength);
    return cleaned || undefined;
  }

  /**
   * Whether to surface support repair actions on order items. Staff only.
   * The "not impersonating" clause used to live here too, but that became
   * redundant once ApiService started routing /support/* requests through
   * the parked admin's JWT during impersonation (PR 6). Now `globals.isSupport`
   * reflects the human at the keyboard regardless of impersonation state,
   * and the repair endpoints work either way — so this is a one-clause gate.
   */
  get canRepairOrderItems(): boolean {
    return this.globals.isSupport;
  }

  orderDetails = signal<any | null>(null);
  loading = signal(true);
  canceling = signal(false);
  duplicating = signal(false);

  // Bank-transfer coordinates fetched lazily for awaiting-payment bank-transfer orders.
  // The order confirmation email links here and expects the customer to see them.
  bankTransferConfig = signal<BankTransferConfig | null>(null);
  bankTransferLoading = signal(false);
  // Derived from the current order — never goes stale on prev/next.
  bankTransferIsEU = computed(() =>
    Number(this.orderDetails()?.payment?.type) === PaymentType.BANK_TRANSFER
  );
  // Holds the resolved orderId so the template's retry button can re-fetch
  // without going back through the eligibility gate.
  bankTransferOrderId = signal<number | null>(null);
  // True when the last fetch attempt failed — drives the retry UI.
  bankTransferError = signal(false);

  // Multibanco coordinates fetched lazily for awaiting-payment Multibanco
  // orders. Same shape as the bank-transfer block above — embedded in the
  // payment-box so the customer can recover the entity/reference from the
  // order confirmation email link.
  multibancoConfig = signal<MultibancoConfig | null>(null);
  multibancoLoading = signal(false);
  multibancoOrderId = signal<number | null>(null);
  multibancoError = signal(false);

  // Boleto (Bradesco) fetched lazily for awaiting-payment BOLETO_V2 orders.
  // Render-only: open/print URL + copyable digit line; confirmation is
  // asynchronous (bank reconciliation cron) so there is nothing to poll.
  boletoConfig = signal<BoletoConfig | null>(null);
  boletoLoading = signal(false);
  boletoOrderId = signal<number | null>(null);
  boletoError = signal(false);

  // Navigation between orders
  currentIndex = signal(0);
  ordersList = signal<any[]>([]);

  // Track expanded groups for showing details
  expandedGroups = signal<Set<number>>(new Set());
  // Track expanded files sections within groups
  expandedFiles = signal<Set<number>>(new Set());
  // Expansion state of the order-extras "fake group" (collapsed by default,
  // like real product groups).
  extrasExpanded = signal(false);

  // Image viewer state
  imageViewerOpen = signal(false);
  viewerImages = signal<any[]>([]);
  currentImageIndex = signal(0);

  // Project thumbnails per group (keyed by group index)
  groupThumbnails = signal<Map<number, {
    groupName: string;
    thumbnails: { label: string; dataUrl: string }[];
    /** Multi-design drafts: what this design changes vs the common base (value '' = same as base). */
    details?: { label: string; value: string }[];
  }[]>>(new Map());

  // Cover thumbnails per group (keyed by group index)
  groupCoverThumbnails = signal<Map<number, string>>(new Map());

  // Navigation computed properties
  canGoPrevious(): boolean {
    return this.currentIndex() > 0;
  }
  
  canGoNext(): boolean {
    const list = this.ordersList();
    return list.length > 0 && this.currentIndex() < list.length - 1;
  }

  ngOnInit(): void {
    // Initialize navigation data
    console.log('[OrderDetailsDialog] Data received:', {
      order: this.data.order,
      ordersList: this.data.ordersList?.length,
      currentIndex: this.data.currentIndex
    });
    
    if (this.data.ordersList && this.data.ordersList.length > 0) {
      this.ordersList.set(this.data.ordersList);
      console.log('[OrderDetailsDialog] Orders list set with', this.data.ordersList.length, 'orders');
    }
    if (this.data.currentIndex !== undefined) {
      this.currentIndex.set(this.data.currentIndex);
    }
    this.loadOrderDetails();
  }

  async loadOrderDetails(): Promise<void> {
    console.log('[OrderDetailsDialog] Loading details for order:', this.data.order);
    this.loading.set(true);
    
    try {
      const details = await firstValueFrom(this.orderService.getOrderDetails(this.data.order));
      console.log('[OrderDetailsDialog] Details loaded:', details);
      
      if (details) {
        console.log('[OrderDetailsDialog] keys:', Object.keys(details));
        console.log('[OrderDetailsDialog] shipmentAddress:', details.shipmentAddress);
        console.log('[OrderDetailsDialog] groups:', details.groups);
        if (details.groups?.[0]) {
          console.log('[OrderDetailsDialog] First group:', details.groups[0]);
          console.log('[OrderDetailsDialog] First group products:', details.groups[0].products);
          if (details.groups[0].products?.[0]) {
            console.log('[OrderDetailsDialog] First product keys:', Object.keys(details.groups[0].products[0]));
            console.log('[OrderDetailsDialog] First product full:', details.groups[0].products[0]);
          }
        }
        console.log('[OrderDetailsDialog] status:', details.status);
        console.log('[OrderDetailsDialog] orderDate:', details.orderDate);
        // Initiate the payment BEFORE rendering/config-fetching so the
        // status badge and the payment-box gates see the post-initiation
        // state (and the boleto/multibanco coordinates exist to fetch).
        // Returns the authoritative (re-fetched) details after initiation.
        // Payment initiation is CART-gated (guide §4.2) — for a no-CART
        // sub-user skip it (it would 403) and render the details as-is.
        const finalDetails = this.perms.canCart()
          ? await this.maybeInitiatePayment(details)
          : details;
        this.orderDetails.set(finalDetails);
        // Resolve the FORM display order for each product's finishes
        this.loadFinishOptionOrder(finalDetails);
        // Load project thumbnails from associated drafts
        this.loadProjectThumbnails(finalDetails);
        // Surface payment-pending coordinates inside the payment-box.
        this.maybeLoadPaymentConfigs(finalDetails);
      } else {
        // Use the original order data if details failed. The cached
        // data.order may carry payment.type from the orders list, and the
        // email-linked landing page must show the coordinates even when
        // getOrderDetails was unavailable.
        console.log('[OrderDetailsDialog] No details returned, using original order');
        this.orderDetails.set(this.data.order);
        this.maybeLoadPaymentConfigs(this.data.order);
      }
    } catch (error) {
      console.error('[OrderDetailsDialog] Error loading details:', error);
      // Use the original order data on error
      this.orderDetails.set(this.data.order);
      this.maybeLoadPaymentConfigs(this.data.order);
    } finally {
      this.loading.set(false);
    }
  }

  getStatusLabel(status: number | string | undefined): string {
    const statusNum = Number(status);
    const statusKeyMap: { [key: number]: string } = {
      0: 'ORDERS.STATUS.CANCELLED',
      1: 'ORDERS.STATUS.PENDING',
      2: 'ORDERS.STATUS.AWAITING_PAYMENT',
      3: 'ORDERS.STATUS.PAYMENT_CONFIRMED',
      4: 'ORDERS.STATUS.PROCESSING',
      5: 'ORDERS.STATUS.IN_PRODUCTION',
      6: 'ORDERS.STATUS.COMPLETED'
    };
    const key = statusKeyMap[statusNum] || 'ORDERS.STATUS.PENDING';
    return this.translate.instant(key);
  }

  getStatusClass(status: number | string | undefined): string {
    const statusNum = Number(status);
    if (statusNum === 0) return 'canceled';
    if (statusNum === 1 || statusNum === 2) return 'pending';
    if (statusNum >= 3 && statusNum <= 5) return 'production';
    if (statusNum === 6) return 'completed';
    return '';
  }

  formatPrice(value: any): string {
    return this.priceService.format(value);
  }

  // Calculate total products value
  getTotalProducts(): string {
    const details = this.orderDetails();
    if (!details) return this.formatPrice(0);
    const base = parseFloat(details.baseValue) || 0;
    const discount = parseFloat(details.totalDiscount) || 0;
    const shipping = parseFloat(details.totalShippingPrice) || 0;
    return this.formatPrice(base + discount - shipping);
  }

  // Calculate subtotal
  getSubTotal(): string {
    const details = this.orderDetails();
    if (!details) return this.formatPrice(0);
    const base = parseFloat(details.baseValue) || 0;
    const shipping = parseFloat(details.totalShippingPrice) || 0;
    return this.formatPrice(base - shipping);
  }

  // Shipping discount = the shipment-type discount PLUS the payment discount
  // applied to shipping. The backend splits them (shipmentType.discount and
  // shipmentType.paymentDiscount), so the displayed "Desconto Portes" must sum
  // both — otherwise a PIX/payment-only shipping discount reads as 0.00.
  getShippingDiscount(): string {
    const shipmentType = this.orderDetails()?.shipmentType;
    const discount = parseFloat(shipmentType?.discount) || 0;
    const paymentDiscount = parseFloat(shipmentType?.paymentDiscount) || 0;
    return this.formatPrice(discount + paymentDiscount);
  }

  /** Numeric discount values for the totals boxes — zero lines render muted,
   *  non-zero ones orange with a minus prefix (totals-boxes design). */
  discountValue(field: 'customer' | 'vouchers' | 'total' | 'shipping'): number {
    const d: any = this.orderDetails();
    if (!d) return 0;
    if (field === 'shipping') {
      return (parseFloat(d.shipmentType?.discount) || 0) + (parseFloat(d.shipmentType?.paymentDiscount) || 0);
    }
    const key = field === 'customer' ? 'customerTotalDiscount' : field === 'vouchers' ? 'vouchersTotalDiscount' : 'totalDiscount';
    return parseFloat(d[key]) || 0;
  }

  /** VAT breakdown visible — the details payload can carry showVat 1 even for
   *  BR orders (vatValue 0), so the flag alone isn't enough. */
  showVatBreakdown(): boolean {
    const d: any = this.orderDetails();
    return !!d && +d.showVat === 1 && +(d.vatValue || 0) > 0;
  }

  // Applied vouchers on the order. Placed-order / order-detail reads now
  // populate order.vouchers (reconstructed from saved redemption rows); only
  // applied entries appear (statusKey REDEEMED / REDEEMED_SHIPPING).
  getAppliedVouchers(): any[] {
    const vouchers = this.orderDetails()?.vouchers;
    if (!Array.isArray(vouchers)) return [];
    return vouchers.filter((v: any) => isAppliedVoucher(v));
  }

  // Order-level estimated shipping date. The backend sends an ISO datetime
  // (e.g. "2026-07-06T00:00:00+01:00"); we show the calendar date part only,
  // with no timezone re-interpretation. Null (→ row hidden) for missing /
  // placeholder values.
  getEstimatedDeliveryDate(): string | null {
    const details = this.orderDetails();
    if (!details) return null;
    // A cancelled order won't ship — never show an estimate. Match only an
    // explicit 0/'0' so a null/blank status (Number('') === 0) isn't mistaken
    // for cancelled and silently hidden.
    if (details.status === 0 || details.status === '0') return null;
    const raw = details.estimatedDeliveryDate;
    if (raw == null) return null;
    // Accept both ISO ("…T…") and MySQL ("YYYY-MM-DD HH:MM:SS") datetimes.
    const datePart = String(raw).split(/[T ]/)[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(datePart) && !datePart.startsWith('0000') ? datePart : null;
  }

  // The estimate note ("…caso o pagamento seja efetuado no próprio dia útil")
  // is payment-conditional, so only show it while payment is still pending
  // (pending / awaiting-payment). Once payment is confirmed it no longer applies.
  showEstimateNote(): boolean {
    const s = Number(this.orderDetails()?.status);
    return s === 1 || s === 2;
  }

  // Urgent order (priority production surcharge) — shown as a header badge.
  isUrgentOrder(): boolean {
    return this.isTruthy(this.orderDetails()?.urgent);
  }

  // A group is a showcase ("mostruário") order when its showcase flag is set.
  isGroupShowcase(group: any): boolean {
    return this.isTruthy(group?.showcase);
  }

  // Server booleans arrive as 1/0, '1'/'0' or true/false — normalize.
  private isTruthy(v: any): boolean {
    return v === true || v === 1 || v === '1';
  }

  // AMF/PHP lists sometimes serialize as an index-keyed object ({"0":…,"1":…})
  // instead of a JSON array — normalize either shape to an array (mirrors the
  // cart's groups/extras handling).
  private toArray(v: any): any[] {
    if (Array.isArray(v)) return v;
    if (v && typeof v === 'object') return Object.values(v);
    return [];
  }

  // Group-level extras (urgency surcharge, etc) — rendered as "Extras › ref"
  // rows inside the group's product table. Skips empty/blank entries.
  getGroupExtraValues(group: any): any[] {
    return this.toArray(group?.extraValues).filter((ev: any) => ev && (ev.ref || ev.name));
  }

  getExtraValueRef(ev: any): string {
    return ev?.ref || ev?.name || '';
  }

  // Net line total for the row, qty-aware (per-unit net `price` × extraQty),
  // matching the net convention of the product price column.
  getExtraValuePrice(ev: any): number {
    const qty = Number(ev?.extraQty) || 1;
    const unit = Number(ev?.price ?? ev?.netPrice ?? 0) || 0;
    return unit * qty;
  }

  // Order-level extras (e.g. "Morada de cliente final") — rendered as a separate
  // "fake group" card after the product groups. Each carries name + priceInfo.price.
  // Require a name so the header total, the names subtitle and the table rows all
  // describe the SAME set (a nameless entry can't be labelled in any of them).
  getOrderExtras(): any[] {
    return this.toArray(this.orderDetails()?.extras).filter((e: any) => e && e.name);
  }

  getOrderExtraName(extra: any): string {
    return extra?.name || '';
  }

  getOrderExtraPrice(extra: any): number {
    return Number(extra?.priceInfo?.price ?? extra?.price ?? 0) || 0;
  }

  getOrderExtrasTotal(): number {
    return this.getOrderExtras().reduce((sum, e) => sum + this.getOrderExtraPrice(e), 0);
  }

  getOrderExtrasNames(): string {
    return this.getOrderExtras().map(e => this.getOrderExtraName(e)).filter(Boolean).join(', ');
  }

  // Get product display name
  getProductDisplayName(product: any): string {
    if (!product) return this.translate.instant('ORDERS.PRODUCT');

    // Try different name sources
    if (product.name && product.name.trim() !== '') {
      return product.name;
    }
    if (product.projectInfo?.name && product.projectInfo.name.trim() !== '') {
      return product.projectInfo.name;
    }
    // Use subCategoryName + productName as fallback
    if (product.subCategoryName && product.productName) {
      return `${product.subCategoryName} - ${product.productName}`;
    }
    if (product.productName) {
      return product.productName;
    }
    return this.translate.instant('ORDERS.PRODUCT');
  }

  getProductPath(group: any): string {
    if (!group) return '';
    // Get product info from first product in group
    const product = group.products?.[0];
    if (!product) return group.name || '';
    
    const parts: string[] = [];
    if (product.subCategoryName) parts.push(product.subCategoryName);
    if (product.productName) parts.push(product.productName);
    
    return parts.join(' › ') || group.name || '';
  }

  // Toggle expanded state for a group
  toggleGroupFiles(groupIndex: number): void {
    const current = this.expandedGroups();
    const newSet = new Set(current);
    if (newSet.has(groupIndex)) {
      newSet.delete(groupIndex);
    } else {
      newSet.add(groupIndex);
    }
    this.expandedGroups.set(newSet);
  }

  // Check if group is expanded
  isGroupExpanded(groupIndex: number): boolean {
    return this.expandedGroups().has(groupIndex);
  }

  toggleExtrasExpanded(): void {
    this.extrasExpanded.update(v => !v);
  }

  // Toggle files expanded state for a group
  toggleFilesExpanded(groupIndex: number): void {
    const current = this.expandedFiles();
    const newSet = new Set(current);
    if (newSet.has(groupIndex)) {
      newSet.delete(groupIndex);
    } else {
      newSet.add(groupIndex);
    }
    this.expandedFiles.set(newSet);
  }

  // Check if files section is expanded
  isFilesExpanded(groupIndex: number): boolean {
    return this.expandedFiles().has(groupIndex);
  }

  // Get group name (project name from first product) - only returns actual name, no fallback
  getGroupName(group: any): string {
    if (!group) return '';
    const product = group.products?.[0];
    if (product?.name) return product.name;
    if (product?.projectInfo?.name) return product.projectInfo.name;
    return group.name || '';
  }

  // Check if the group is an album product (has pages)
  isAlbumGroup(group: any): boolean {
    if (!group) return false;
    const product = group.products?.[0];
    if (!product) return false;

    // Check if it has pages (albums have pages)
    const hasPages = (product.numPages && product.numPages > 0) ||
                     (product.pages && product.pages > 0) ||
                     (group.pages && group.pages > 0) ||
                     (group.numPages && group.numPages > 0) ||
                     (product.projectInfo?.pages && product.projectInfo.pages > 0);

    // Also check category names for album-related keywords
    const categoryName = (product.subCategoryName || '').toLowerCase();
    const productName = (product.productName || '').toLowerCase();
    const isAlbumCategory = categoryName.includes('album') || categoryName.includes('álbum') ||
                           productName.includes('album') || productName.includes('álbum');

    return hasPages || isAlbumCategory;
  }

  // Get group description (product type + size)
  getGroupDescription(group: any): string {
    if (!group) return '';
    const product = group.products?.[0];
    if (!product) return '';
    
    const parts: string[] = [];
    if (product.productName) parts.push(product.productName);
    if (product.orientation) parts.push(product.orientation);
    if (product.subCategoryName) parts.push(product.subCategoryName);
    
    return parts.join(' - ') || '';
  }

  // Get number of pages/sheets from group
  getGroupPages(group: any): string {
    if (!group) return '-';
    
    // First check the main product's numPages (server often puts it here)
    const product = group.products?.[0];
    console.log('[OrderDetailsDialog] getGroupPages - product:', product);
    console.log('[OrderDetailsDialog] getGroupPages - product.numPages:', product?.numPages);
    console.log('[OrderDetailsDialog] getGroupPages - product.pages:', product?.pages);
    console.log('[OrderDetailsDialog] getGroupPages - product.files?.length:', product?.files?.length);
    
    // If numPages is set and not 0, use it
    if (product?.numPages !== undefined && product.numPages !== null && product.numPages > 0) {
      return String(product.numPages);
    }
    if (product?.pages !== undefined && product.pages !== null && product.pages > 0) {
      return String(product.pages);
    }
    
    // Try group level
    if (group.pages && group.pages > 0) return String(group.pages);
    if (group.numPages && group.numPages > 0) return String(group.numPages);
    if (group.sheets && group.sheets > 0) return String(group.sheets);
    
    // Check extraValues array for pages
    if (group.extraValues && Array.isArray(group.extraValues)) {
      const pagesExtra = group.extraValues.find((ev: any) => ev.key === 'pages');
      if (pagesExtra && pagesExtra.value) return String(pagesExtra.value);
    }
    
    // More product checks
    if (product?.sheets && product.sheets > 0) return String(product.sheets);
    if (product?.projectInfo?.pages && product.projectInfo.pages > 0) return String(product.projectInfo.pages);
    if (product?.projectInfo?.numPages && product.projectInfo.numPages > 0) return String(product.projectInfo.numPages);
    
    // Count files as fallback - this is reliable for albums
    if (product?.files?.length > 0) return String(product.files.length);
    
    return '-';
  }

  // Get order_item_group_id for the group header
  getOrderItemGroupId(group: any): string {
    if (!group) return '';
    // Try various field names for the group ID
    if (group.orderItemGroupId) return String(group.orderItemGroupId);
    if (group.groupId) return String(group.groupId);
    if (group.id) return String(group.id);
    // Try from first product
    const product = group.products?.[0];
    if (product?.orderItemGroupId) return String(product.orderItemGroupId);
    if (product?.groupId) return String(product.groupId);
    return '';
  }

  // Get full product name with category
  getProductFullName(product: any): string {
    if (!product) return this.translate.instant('ORDERS.PRODUCT');
    const parts: string[] = [];

    if (product.subCategoryName) parts.push(product.subCategoryName);
    if (product.productName) parts.push(product.productName);
    if (product.orientation) parts.push(`(${product.orientation})`);

    if (parts.length === 0) {
      if (product.name) return product.name;
      return this.translate.instant('ORDERS.PRODUCT');
    }

    return parts.join(' › ');
  }

  // Get finish option name
  getFinishOptionName(finish: any): string {
    return finish?.optionName || finish?.name || this.translate.instant('ORDERS.FINISH');
  }

  // ===== Finish display order (FORM order) =====
  // The order_item_finish table's composite PK (order_item_id, finish_id,
  // finish_option_id) makes the backend return finishes clustered by
  // FINISH id — meaningless for display. Fetch each product's finish
  // options (cached service call, same one the draft dialog uses) and sort
  // rows by the option's position in the API array — exactly the order the
  // form showed them. Until/unless options load, payload order is kept.
  private readonly finishOptionOrder = signal<Map<number, number>>(new Map());

  private loadFinishOptionOrder(details: any): void {
    const groups = details?.groups;
    if (!Array.isArray(groups)) return;
    const seen = new Set<number>();
    for (const group of groups) {
      const products = group?.products;
      if (!Array.isArray(products)) continue;
      // Album groups fetch with formType 2, everything else 1 — the same
      // split the draft details dialog applies.
      const isAlbumGroup = products.some((p: any) => Number(p?.productTypeId) === 1);
      for (const p of products) {
        const productId = Number(p?.productId) || 0;
        if (!productId || seen.has(productId)) continue;
        if (!Array.isArray(p?.finishes) || p.finishes.length === 0) continue;
        seen.add(productId);
        this.productsService.getFinishOptionsWithCache(productId, isAlbumGroup ? 2 : 1).subscribe({
          next: ({ options }: { options: any[] }) => {
            if (!Array.isArray(options) || options.length === 0) return;
            this.finishOptionOrder.update(map => {
              const next = new Map(map);
              options.forEach((opt: any, index: number) => {
                const id = Number(opt?.id);
                if (id && !next.has(id)) next.set(id, index);
              });
              return next;
            });
          },
          error: () => { /* keep payload order for this product */ }
        });
      }
    }
  }

  /** Finishes sorted into the FORM's option order; unknown options keep
   *  their payload position after the ordered ones. */
  getSortedFinishes(product: any): any[] {
    const finishes = product?.finishes;
    if (!Array.isArray(finishes)) return [];
    const orderMap = this.finishOptionOrder();
    if (orderMap.size === 0) return finishes;
    // Order payload rows carry the option id as `optionId`; some legacy
    // shapes use `finishOptionId` — accept both.
    const optionIdOf = (finish: any): number =>
      Number(finish?.optionId ?? finish?.finishOptionId) || 0;
    return finishes
      .map((finish: any, index: number) => ({ finish, index }))
      .sort((a, b) => {
        const oa = orderMap.get(optionIdOf(a.finish));
        const ob = orderMap.get(optionIdOf(b.finish));
        const ka = typeof oa === 'number' ? oa : 100000 + a.index;
        const kb = typeof ob === 'number' ? ob : 100000 + b.index;
        return ka - kb;
      })
      .map(entry => entry.finish);
  }

  // Stamping text values can contain symbol codepoints (U+2100–U+27FF, e.g.
  // U+2680 shield) that only the StampingSymbols font renders with the brand
  // glyphs — same codepoint detection as draft-details-dialog.addFinishValues.
  isStampingTextValue(finish: any): boolean {
    return /[℀-⟿]/.test(this.getFinishValue(finish));
  }

  // Get finish value (returns empty string for NONE values)
  getFinishValue(finish: any): string {
    const rawName = finish?.name || finish?.value || '';
    const dataValue = finish?.data ?? finish?.text ?? '';
    const optionTypeId = Number(finish?.optionTypeId);
    const isTextInput = optionTypeId === 12;

    if (isTextInput && dataValue) {
      return String(dataValue);
    }

    const value = rawName || dataValue || '';
    // Don't display NONE values
    if (typeof value === 'string' && value.toUpperCase() === 'NONE') {
      if (dataValue) {
        return String(dataValue);
      }
      return '';
    }
    return typeof value === 'string' ? value : String(value);
  }

  // Get group/project ID (order_item_id from first product)
  getGroupId(group: any): string {
    if (!group) return '';
    // Try group code first
    if (group.code) return String(group.code);
    // Try first product's orderItemId
    const product = group.products?.[0];
    if (product?.orderItemId) return String(product.orderItemId);
    if (product?.id) return String(product.id);
    return '';
  }

  // Get product item ID
  getProductItemId(product: any): string {
    if (!product) return '';
    if (product.orderItemId) return String(product.orderItemId);
    if (product.itemId) return String(product.itemId);
    if (product.id) return String(product.id);
    return '';
  }

  // Duplicate order — shows dialog for name (+ group selection if multiple), then duplicates
  async duplicateOrder(): Promise<void> {
    const details = this.orderDetails();
    if (!details?.groups?.length) return;

    const draftNameData = {
      showDraftName: true,
      draftNameLabel: this.translate.instant('ORDERS.DUPLICATE_DRAFT_NAME_LABEL'),
      draftNamePlaceholder: this.translate.instant('ORDERS.DUPLICATE_DRAFT_NAME_PLACEHOLDER'),
      draftNameRequired: this.translate.instant('ORDERS.DUPLICATE_DRAFT_NAME_REQUIRED'),
      defaultDraftName: '',
      showScopeChoice: true,
      scopeLabel: this.translate.instant('ORDERS.DUPLICATE_SCOPE_LABEL'),
      scopeOptions: [
        { value: 'without', label: this.translate.instant('ORDERS.DUPLICATE_SCOPE_WITHOUT') },
        { value: 'with', label: this.translate.instant('ORDERS.DUPLICATE_SCOPE_WITH') }
      ],
      defaultScope: 'without'
    };

    let selectedGroup: any;
    let draftName: string;
    let withFiles = false;

    if (details.groups.length === 1) {
      // Single group — show dialog with only the name input
      selectedGroup = details.groups[0];
      draftNameData.defaultDraftName = selectedGroup.name || '';

      const result = await this.showDuplicateDialog([], draftNameData);
      if (!result) return; // User canceled
      draftName = result.draftName;
      withFiles = result.scope === 'with';
    } else {
      // Multiple groups — show selection dialog + name input
      const groupOptions = details.groups.map((g: any, i: number) => {
        const mainProduct = g.products?.[0];
        const label = g.name || mainProduct?.productName || `Grupo ${i + 1}`;
        return { label, group: g };
      });

      const result = await this.showDuplicateDialog(groupOptions, draftNameData);
      if (!result) return; // User canceled
      selectedGroup = result.group;
      draftName = result.draftName;
      withFiles = result.scope === 'with';
    }

    // Validate draftId exists
    if (!selectedGroup.draftId) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.translate.instant('ORDERS.DUPLICATE_ORDER'),
          message: this.translate.instant('ORDERS.DUPLICATE_NO_DRAFT'),
          cancelText: null
        }
      });
      return;
    }

    // Proceed with duplication
    await this.executeDuplicate(selectedGroup, draftName, withFiles);
  }

  private showDuplicateDialog(
    groups: { label: string; group: any }[],
    nameConfig: { showDraftName: boolean; draftNameLabel: string; draftNamePlaceholder: string; draftNameRequired: string; defaultDraftName: string; showScopeChoice?: boolean; scopeLabel?: string; scopeOptions?: { value: string; label: string }[]; defaultScope?: string }
  ): Promise<any | null> {
    return new Promise(resolve => {
      const title = groups.length > 0
        ? this.translate.instant('ORDERS.SELECT_GROUP_TO_DUPLICATE')
        : this.translate.instant('ORDERS.DUPLICATE_DRAFT_TITLE');

      const dialogRef = this.dialog.open(GroupSelectionDialogComponent, {
        width: '400px',
        panelClass: 'confirm-dialog-panel',
        data: {
          title,
          groups,
          ...nameConfig
        }
      });
      dialogRef.afterClosed().subscribe(result => {
        resolve(result || null);
      });
    });
  }

  private async executeDuplicate(group: any, draftName: string, withFiles: boolean): Promise<void> {
    this.duplicating.set(true);

    try {
      // 1. Build DraftInfo to load the original draft
      const user = this.userState.user();
      const draftInfo: DraftInfo = {
        id: group.draftId,
        serverName: '',
        serverId: 0,
        pathStructureId: 0,
        originalName: group.name || '',
        customerId: user?.id || 0,
        productTypeId: group.products?.[0]?.productTypeId || 0,
        creationDate: '',
        lastModificationDate: ''
      };

      // 2. Load original draft from server
      console.log('[OrderDetailsDialog] Loading draft for duplication:', draftInfo.id);
      const draftData = await firstValueFrom(this.draftService.loadDraft(draftInfo));

      if (!draftData) {
        throw new Error('Failed to load draft data');
      }

      // 3. Build the draft to save. "Without files" strips all file/photo/
      //    project references (selections only). "With files" keeps the file
      //    references (photos are reused by serverName/md5 — no re-upload) and
      //    clones the editor projects so the duplicate is independent; if a
      //    clone can't be made independent the source refs are kept (shared
      //    project) — same fallback as the drafts-list duplicate.
      let draftToSave: DraftData;
      if (withFiles) {
        // Deep-clone before mutating refs: loadDraft may return a shared/cached
        // object and cloneDraftProjects mutates projectId fields in place.
        draftToSave = JSON.parse(JSON.stringify(draftData)) as DraftData;
        draftToSave.hasFiles = true;
        await this.draftDuplication.cloneDraftProjects(draftToSave);
      } else {
        draftToSave = this.stripFilesFromDraft(draftData);
      }

      // 4. Save as new draft with user-provided name
      const productTypeId = group.products?.[0]?.productTypeId || 0;

      console.log('[OrderDetailsDialog] Saving duplicated draft:', draftName, 'withFiles:', withFiles);
      const savedDraftInfo = await firstValueFrom(
        this.draftService.saveDraft(draftName, draftToSave, productTypeId)
      );

      console.log('[OrderDetailsDialog] Draft saved:', savedDraftInfo);

      // 5. Close dialog with result (orders component handles navigation)
      this.dialogRef.close({
        action: 'duplicated' as const,
        orderId: this.orderDetails()?.orderId,
        draftInfo: savedDraftInfo
      });

    } catch (err) {
      console.error('[OrderDetailsDialog] Duplicate failed:', err);
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.translate.instant('COMMON.ERROR'),
          message: this.translate.instant('ORDERS.DUPLICATE_ERROR'),
          cancelText: null
        }
      });
    } finally {
      this.duplicating.set(false);
    }
  }

  private stripFilesFromDraft(draft: DraftData): DraftData {
    const clean: DraftData = {
      ...draft,
      hasFiles: false,
      totalPrice: undefined,
      draftThumbnail: undefined,
      createdAt: new Date()
    };

    if (clean.albumConfig) {
      clean.albumConfig = this.stripAlbumConfigFiles(clean.albumConfig);
    }
    if (clean.productConfig) {
      clean.productConfig = this.stripProductConfigFiles(clean.productConfig);
    }

    return clean;
  }

  private stripAlbumConfigFiles(config: DraftAlbumConfig): DraftAlbumConfig {
    return {
      ...config,
      // Strip album files/photos/projects
      albumPagesFinishFiles: {},
      uploadedPhotos: [],
      albumProjectId: undefined,
      albumProjectServerName: undefined,
      albumProjectServerId: undefined,
      albumProjectPathStructureId: undefined,
      albumPageThumbnails: undefined,
      coverThumbnail: undefined,
      albumProjectPageCount: undefined,
      // Strip cover projects
      coverProjectId: undefined,
      coverProjectServerName: undefined,
      coverProjectServerId: undefined,
      coverProjectPathStructureId: undefined,
      coverProjectTransparentThumbnail: undefined,
      coverProjectThumbnailPosition: undefined,
      coverProjectSpineWidth: undefined,
      // Strip storytelling data (photo chapters)
      storytellingData: undefined,
      storytellingWip: undefined,
      // Strip cover finish files and panel projects
      cover: config.cover ? {
        ...config.cover,
        finishFiles: {},
        panels: config.cover.panels?.map(p => ({
          ...p,
          finishFiles: {},
          projectId: undefined,
          projectServerName: undefined,
          projectServerId: undefined,
          projectPathStructureId: undefined,
          projectThumbnails: undefined,
          selectedProduct: p.selectedProduct ? {
            ...p.selectedProduct,
            finishFiles: {}
          } : undefined
        }))
      } : config.cover,
      // Strip panel finish files and projects
      panels: config.panels?.map(p => ({
        ...p,
        finishFiles: {},
        projectId: undefined,
        projectServerName: undefined,
        projectServerId: undefined,
        projectPathStructureId: undefined,
        projectThumbnails: undefined,
        selectedProduct: p.selectedProduct ? {
          ...p.selectedProduct,
          finishFiles: {}
        } : undefined
      }))
    };
  }

  private stripProductConfigFiles(config: DraftProductConfig): DraftProductConfig {
    return {
      ...config,
      finishFiles: {},
      uploadedPhotos: [],
      projectId: undefined,
      projectServerName: undefined,
      projectServerId: undefined,
      projectPathStructureId: undefined,
      projectThumbnails: undefined,
      projects: undefined,
      panels: config.panels?.map(p => ({
        ...p,
        finishFiles: {},
        projectId: undefined,
        projectServerName: undefined,
        projectServerId: undefined,
        projectPathStructureId: undefined,
        projectThumbnails: undefined,
        selectedProduct: p.selectedProduct ? {
          ...p.selectedProduct,
          finishFiles: {}
        } : undefined
      }))
    };
  }

  groupHasFiles(group: any): boolean {
    const products = group?.products;
    if (!products || !Array.isArray(products)) return false;
    return products.some((p: any) => this.getProductFiles(p).length > 0);
  }

  // Get files for a product
  getProductFiles(product: any): any[] {
    if (!product) return [];
    // Files could be in different properties depending on API
    const files: any[] = product.files || product.images || [];
    // Natural (numeric) filename order — same rule as the upload store
    // (file-upload-state naturalCompareByName): the API returns files in
    // upload-completion order, not the customer's sheet order.
    return [...files].sort((a, b) =>
      ((a?.name ?? a?.serverName ?? '') as string).localeCompare(
        (b?.name ?? b?.serverName ?? '') as string, undefined, { numeric: true, sensitivity: 'base' }));
  }
  
  /**
   * Group files by finishOptionId to display them with proper headers
   * Returns an array of {header: string, files: any[]}
   */
  getGroupedProductFiles(product: any): Array<{header: string, files: any[]}> {
    const allFiles = this.getProductFiles(product);
    if (allFiles.length === 0) return [];
    
    // Group files by finishOptionId
    const fileGroups = new Map<number, any[]>();
    
    for (const file of allFiles) {
      const finishOptionId = file.finishOptionId !== undefined ? file.finishOptionId : -1;
      if (!fileGroups.has(finishOptionId)) {
        fileGroups.set(finishOptionId, []);
      }
      fileGroups.get(finishOptionId)!.push(file);
    }
    
    // Convert to array with headers
    const result: Array<{header: string, files: any[]}> = [];

    for (const [finishOptionId, files] of fileGroups) {
      // Sub-header is the finish path ("Templates › Template Personalizado") so
      // the operator sees which finish a file group belongs to. Main-product
      // files and failed lookups get an empty header — the per-product heading
      // in the template already identifies them, so a redundant row is hidden.
      let header = '';
      if (finishOptionId > 0) {
        const finishOption = product.finishes?.find((f: any) => f.finishOptionId === finishOptionId);
        if (finishOption) {
          header = `${this.getFinishOptionName(finishOption)} › ${this.getFinishValue(finishOption)}`;
        }
      }
      result.push({ header, files });
    }

    return result;
  }

  /**
   * Check if file is a displayable image type
   * Returns false for PDF, AI, EPS, PSD and other non-image formats
   */
  isImageFile(file: any): boolean {
    if (!file) return false;
    
    const fileName = (file.name || file.serverName || '').toLowerCase();
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.tif'];
    
    return imageExtensions.some(ext => fileName.endsWith(ext));
  }

  /** Vector thumbs (thumbUrl) that failed to load — fall back to the icon. */
  private failedVectorThumbs = new Set<string>();
  /** path/serverName/name → render thumb, harvested from the draft JSON
   *  (the order endpoint's product.files don't carry thumbUrl). */
  private vectorThumbByKey = new Map<string, string>();

  /**
   * Rendered preview for VECTOR files (.ai/.pdf/.eps): these never carry
   * sizes[], but the draft persists the render thumb the upload produced
   * (`thumbUrl` — a data-URL PNG). Resolved from the file itself when
   * present, else from the thumbs harvested out of the group's draft JSON.
   * Null for real images (they use the normal sizes pipeline).
   */
  getVectorThumb(file: any): string | null {
    if (!file || this.isImageFile(file)) return null;
    const keys = [file.path, file.serverName, file.server_name, file.name].filter(
      (k: any): k is string => typeof k === 'string' && !!k
    );
    if (keys.some(k => this.failedVectorThumbs.has(k))) return null;
    const own = file.thumbUrl || file.thumb_url;
    if (typeof own === 'string' && (own.startsWith('data:image') || /^https?:\/\//i.test(own))) {
      return own;
    }
    for (const k of keys) {
      const harvested = this.vectorThumbByKey.get(k);
      if (harvested) return harvested;
    }
    return null;
  }

  onVectorThumbError(file: any): void {
    for (const key of [file?.path, file?.serverName, file?.server_name, file?.name]) {
      if (typeof key === 'string' && key) this.failedVectorThumbs.add(key);
    }
  }

  /**
   * Get file extension from file name
   */
  getFileExtension(file: any): string {
    if (!file) return '';

    const fileName = (file.name || file.serverName || '').toLowerCase();
    const lastDot = fileName.lastIndexOf('.');

    if (lastDot === -1) return '';
    return fileName.substring(lastDot + 1).toUpperCase();
  }

  /**
   * Get icon name for non-image file types
   */
  getFileIcon(file: any): string {
    const ext = this.getFileExtension(file).toLowerCase();
    
    switch (ext) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'ai':
      case 'eps':
        return 'design_services';
      case 'psd':
        return 'photo_library';
      case 'doc':
      case 'docx':
        return 'description';
      case 'xls':
      case 'xlsx':
        return 'table_chart';
      case 'zip':
      case 'rar':
        return 'folder_zip';
      default:
        return 'insert_drive_file';
    }
  }

  // Build the image URL from file info using PathService
  // Path structure: first 6 chars of serverName split into 3 pairs for directory structure
  // Example: serverName=610c14a7b2ad473b148aff3a4ada54b0.png -> /61/0c/14/610c14a7b2ad473b148aff3a4ada54b0.png
  getFileImageUrl(file: any, useThumbnail: boolean = true): string {
    if (!file) return '';

    // Vector files (.ai/.pdf/.eps) have no sizes[] — the persisted render
    // thumb is their only displayable form (viewer included).
    const vectorThumb = this.getVectorThumb(file);
    if (vectorThumb) return vectorThumb;

    // Try to get thumbnail first if requested
    if (useThumbnail && file.sizes && file.sizes.length > 0) {
      const thumb = file.sizes[0];
      if (thumb.serverName) {
        // Create ServerFile object for thumbnail
        const thumbFile: ServerFile = {
          id: file.id || 0,
          serverId: Number(thumb.serverId ?? file.serverId ?? 0),
          serverName: thumb.serverName || thumb.name,
          pathStructureId: Number(thumb.pathStructureId ?? file.pathStructureId ?? 0)
        };
        return this.pathService.getUrl(thumbFile);
      }
    }

    // For viewer mode on direct upload files, prefer _b size over original
    if (!useThumbnail && file.serverName && file.sizes && file.sizes.length > 0) {
      const preferredSuffixes = ['_b', '_m', '_s'];
      for (const suffix of preferredSuffixes) {
        const sizeEntry = file.sizes.find((s: any) => {
          const name: string = s.serverName || s.name || '';
          return name.includes(suffix);
        });
        if (sizeEntry?.serverName) {
          const sizeFile: ServerFile = {
            id: file.id || 0,
            serverId: Number(sizeEntry.serverId ?? file.serverId ?? 0),
            serverName: sizeEntry.serverName || sizeEntry.name,
            pathStructureId: Number(sizeEntry.pathStructureId ?? file.pathStructureId ?? 0)
          };
          return this.pathService.getUrl(sizeFile);
        }
      }
    }

    // Use main file
    if (file.serverName) {
      const serverFile: ServerFile = {
        id: file.id || 0,
        serverId: Number(file.serverId ?? 0),
        serverName: file.serverName,
        pathStructureId: Number(file.pathStructureId ?? 0)
      };
      // When the backend gave us no `sizes` entry, derive the small `_t` thumbnail by
      // convention (abc.jpg -> abc_t.jpg) for the grid miniatures instead of downloading
      // the multi-MB original. The viewer (useThumbnail=false) still gets the full image,
      // and the <img> (error) handler falls back to this original if a `_t` is missing.
      return useThumbnail
        ? this.pathService.getThumbnailUrl(serverFile)
        : this.pathService.getUrl(serverFile);
    }
    
    // Fallback - try direct url properties (including data URLs from project thumbnails)
    return file.thumbUrl || file.thumb || file.url || file.path || '';
  }

  // Progressively-larger variants tried by onThumbnailError when a smaller one is
  // missing on storage. '' = the full-size original (last resort).
  private readonly thumbnailFallbackSuffixes = ['_t', '_s', '_m', '_b', ''];

  /** Build a sized-variant URL by swapping the size suffix before the extension (abc.jpg -> abc_s.jpg; '' = original). */
  private buildSizedUrl(file: any, suffix: string): string {
    if (!file?.serverName) return '';
    const serverName: string = file.serverName;
    const lastDot = serverName.lastIndexOf('.');
    const rawBase = lastDot > 0 ? serverName.substring(0, lastDot) : serverName;
    const ext = lastDot > 0 ? serverName.substring(lastDot) : '';
    const base = rawBase.replace(/_(ts|t|s|m|b|l|e)$/, '');
    const serverFile: ServerFile = {
      id: file.id || 0,
      serverId: Number(file.serverId ?? 0),
      serverName: suffix ? `${base}${suffix}${ext}` : `${base}${ext}`,
      pathStructureId: Number(file.pathStructureId ?? 0)
    };
    return this.pathService.getUrl(serverFile);
  }

  /**
   * Thumbnail fallback cascade: the grid starts at the small `_t` variant; if a
   * variant 404s (e.g. an older file that never had that size generated), step up
   * to the next larger one (_t -> _s -> _m -> _b -> original) so the miniature
   * still renders. Stops at the original to avoid an infinite error loop.
   */
  onThumbnailError(event: Event, file: any): void {
    const img = event.target as HTMLImageElement | null;
    if (!img || !file?.serverName) return;
    const step = Number(img.dataset['fallbackStep'] ?? '0');
    const nextStep = step + 1;
    if (nextStep >= this.thumbnailFallbackSuffixes.length) return;
    const nextUrl = this.buildSizedUrl(file, this.thumbnailFallbackSuffixes[nextStep]);
    img.dataset['fallbackStep'] = String(nextStep);
    if (nextUrl && img.src !== nextUrl) {
      img.src = nextUrl;
    }
  }

  print(): void {
    console.log('[OrderDetailsDialog] Print order');
    // TODO: Implement print/PDF
    window.print();
  }

  // Check if order can be canceled (status 1 = Aguardar Pagamento, status 2 = Verificar Pagamento)
  canCancel(): boolean {
    const details = this.orderDetails();
    if (!details) return false;
    const status = Number(details.status);
    return status === 1 || status === 2;
  }

  // Cancel order
  async cancelOrder(): Promise<void> {
    const details = this.orderDetails();
    if (!details || !this.canCancel()) return;

    // Show confirmation dialog
    const confirmRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      panelClass: 'confirm-dialog-panel',
      data: {
        title: this.translate.instant('ORDERS.CANCEL_ORDER_TITLE'),
        message: this.translate.instant('ORDERS.CANCEL_ORDER_CONFIRM'),
        confirmText: this.translate.instant('ORDERS.YES_CANCEL'),
        cancelText: this.translate.instant('COMMON.NO'),
        confirmColor: 'warn'
      }
    });

    const confirmed = await firstValueFrom(confirmRef.afterClosed());
    if (!confirmed) return;

    const orderId = details.orderId || details.id || this.data.order.orderId || this.data.order.id;
    console.log('[OrderDetailsDialog] Canceling order:', orderId);

    this.canceling.set(true);

    try {
      const result = await firstValueFrom(this.orderService.cancelOrder(orderId));
      console.log('[OrderDetailsDialog] Cancel result:', result);

      // REST endpoint returns { success: true, message, _token } on success;
      // cancelOrder() maps HTTP errors to `false`. (The old `=== 'OK'` check
      // was a leftover AMF convention and never matched, so a successful
      // cancel showed a bogus "[object Object]" error.)
      if (result && result.success === true) {
        // Close dialog with result so parent can refresh
        this.dialogRef.close({ action: 'canceled', orderId } as OrderDetailsDialogResult);
      } else {
        // false on HTTP error, or { success:false, message } on a server-side
        // rejection — surface the server message when present.
        const serverMessage = (result && typeof result === 'object' && result.message)
          ? result.message
          : this.translate.instant('ERRORS.GENERIC');
        this.dialog.open(ConfirmDialogComponent, {
          width: '400px',
          panelClass: 'confirm-dialog-panel',
          data: {
            title: this.translate.instant('COMMON.ERROR'),
            message: this.translate.instant('ORDERS.CANCEL_ORDER_ERROR') + ': ' + serverMessage,
            confirmText: this.translate.instant('COMMON.OK'),
            cancelText: null
          }
        });
      }
    } catch (error) {
      console.error('[OrderDetailsDialog] Cancel error:', error);
      this.dialog.open(ConfirmDialogComponent, {
        width: '400px',
        panelClass: 'confirm-dialog-panel',
        data: {
          title: this.translate.instant('COMMON.ERROR'),
          message: this.translate.instant('ORDERS.CANCEL_ORDER_ERROR_RETRY'),
          confirmText: this.translate.instant('COMMON.OK'),
          cancelText: null
        }
      });
    } finally {
      this.canceling.set(false);
    }
  }

  // Navigate to previous order
  goToPreviousOrder(): void {
    if (!this.canGoPrevious()) return;

    const newIndex = this.currentIndex() - 1;
    const list = this.ordersList();

    this.currentIndex.set(newIndex);
    this.data.order = list[newIndex];

    // Reset expanded states and thumbnails
    this.expandedGroups.set(new Set());
    this.expandedFiles.set(new Set());
    this.extrasExpanded.set(false);
    this.groupThumbnails.set(new Map());
    this.groupCoverThumbnails.set(new Map());
    this.bankTransferConfig.set(null);
    this.bankTransferError.set(false);
    this.bankTransferOrderId.set(null);
    this.bankTransferLoading.set(false);
    this.multibancoConfig.set(null);
    this.multibancoError.set(false);
    this.multibancoOrderId.set(null);
    this.multibancoLoading.set(false);
    this.boletoConfig.set(null);
    this.boletoError.set(false);
    this.boletoOrderId.set(null);
    this.boletoLoading.set(false);
    this.cieloUrl.set(null);
    this.cieloError.set(false);
    this.cieloOrderId.set(null);
    this.cieloLoading.set(false);
    this.vivaOrderCode.set(null);
    this.vivaError.set(false);
    this.vivaOrderId.set(null);
    this.vivaLoading.set(false);
    this.vivaProcessing.set(false);
    this.vivaRetry.set(false);
    this.vivaAborting.set(false);

    this.notifyOrderChanged(list[newIndex]);

    // Reload details for new order
    this.loadOrderDetails();
  }

  // Navigate to next order
  goToNextOrder(): void {
    if (!this.canGoNext()) return;

    const newIndex = this.currentIndex() + 1;
    const list = this.ordersList();

    this.currentIndex.set(newIndex);
    this.data.order = list[newIndex];

    // Reset expanded states and thumbnails
    this.expandedGroups.set(new Set());
    this.expandedFiles.set(new Set());
    this.extrasExpanded.set(false);
    this.groupThumbnails.set(new Map());
    this.groupCoverThumbnails.set(new Map());
    this.bankTransferConfig.set(null);
    this.bankTransferError.set(false);
    this.bankTransferOrderId.set(null);
    this.bankTransferLoading.set(false);
    this.multibancoConfig.set(null);
    this.multibancoError.set(false);
    this.multibancoOrderId.set(null);
    this.multibancoLoading.set(false);
    this.boletoConfig.set(null);
    this.boletoError.set(false);
    this.boletoOrderId.set(null);
    this.boletoLoading.set(false);
    this.cieloUrl.set(null);
    this.cieloError.set(false);
    this.cieloOrderId.set(null);
    this.cieloLoading.set(false);
    this.vivaOrderCode.set(null);
    this.vivaError.set(false);
    this.vivaOrderId.set(null);
    this.vivaLoading.set(false);
    this.vivaProcessing.set(false);
    this.vivaRetry.set(false);
    this.vivaAborting.set(false);

    this.notifyOrderChanged(list[newIndex]);

    // Reload details for new order
    this.loadOrderDetails();
  }

  private notifyOrderChanged(order: any): void {
    const orderId = Number(order?.id || order?.orderId);
    if (orderId && this.data.onOrderChanged) {
      this.data.onOrderChanged(orderId);
    }
  }

  // ========== Image Viewer Methods ==========

  /**
   * Open the image viewer with all images from the specified group
   * @param groupIdx The index of the group
   * @param clickedFile Optional - the file that was clicked to open the viewer
   */
  openImageViewer(groupIdx: number, clickedFile?: any): void {
    const details = this.orderDetails();
    if (!details?.groups?.[groupIdx]) return;

    const group = details.groups[groupIdx];
    const allImages: any[] = [];

    // Collect all image files from all products in the group
    if (group.products) {
      for (const product of group.products) {
        const files = this.getProductFiles(product);
        for (const file of files) {
          // Vector files join the viewer through their persisted render thumb.
          if (this.isImageFile(file) || this.getVectorThumb(file)) {
            allImages.push(file);
          }
        }
      }
    }

    if (allImages.length === 0) return;

    // Find the index of the clicked file in the collected images
    let startIndex = 0;
    if (clickedFile) {
      const clickedIdx = allImages.findIndex(img =>
        img.serverName === clickedFile.serverName ||
        img.id === clickedFile.id ||
        img.name === clickedFile.name
      );
      if (clickedIdx !== -1) {
        startIndex = clickedIdx;
      }
    }

    this.viewerImages.set(allImages);
    this.currentImageIndex.set(startIndex);
    this.imageViewerOpen.set(true);

    // Prevent body scroll when viewer is open
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close the image viewer
   */
  closeImageViewer(): void {
    this.imageViewerOpen.set(false);
    this.viewerImages.set([]);
    this.currentImageIndex.set(0);

    // Restore body scroll
    document.body.style.overflow = '';
  }

  /**
   * Navigate to previous image
   */
  previousImage(): void {
    const current = this.currentImageIndex();
    if (current > 0) {
      this.currentImageIndex.set(current - 1);
    }
  }

  /**
   * Navigate to next image
   */
  nextImage(): void {
    const current = this.currentImageIndex();
    const total = this.viewerImages().length;
    if (current < total - 1) {
      this.currentImageIndex.set(current + 1);
    }
  }

  /**
   * Download a file
   */
  downloadFile(file: any): void {
    if (!file) return;

    const url = this.getFileImageUrl(file, false);
    if (!url) return;

    const fileName = file.name || file.serverName || 'image.jpg';

    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';

    // For cross-origin URLs, we need to fetch and create blob
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        link.href = blobUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        // Fallback: just open in new tab if download fails
        window.open(url, '_blank');
      });
  }

  // ========== Bank Transfer Methods ==========

  /**
   * Fetch bank-transfer coordinates for the order when both:
   *  - the payment type is EU bank transfer (4) or BR bank transfer (11), AND
   *  - the order has not yet had payment confirmed.
   * Status 1 (PENDING) and 2 (AWAITING_PAYMENT) both qualify — bank-transfer
   * orders can sit at either pre-confirmation. Confirmed (3+) or cancelled (0)
   * orders skip the fetch.
   *
   * Drives the "Bank transfer instructions" block on the order-details
   * page that the order confirmation email links to. On failure, the block
   * surfaces a retry button instead of disappearing silently — the email
   * landing page is the customer's primary surface for these coordinates.
   */
  /**
   * Prefetch the payment-pending coordinates for the order's payment type.
   * Exactly one of these will fetch (an order has one payment type); all
   * gate internally on payment.type + status. CART-gated as a whole
   * (guide §4.2): a no-CART sub-user gets no payment surfaces and the
   * fetches would 403 with a spurious "no access" toast.
   */
  private maybeLoadPaymentConfigs(details: any): void {
    if (!this.perms.canCart()) return;
    this.maybeLoadBankTransferConfig(details);
    this.maybeLoadMultibancoConfig(details);
    this.maybeLoadBoletoConfig(details);
    this.maybeLoadCieloConfig(details);
    this.maybeLoadVivaConfig(details);
  }

  private async maybeLoadBankTransferConfig(details: any): Promise<void> {
    const paymentType = Number(details?.payment?.type);
    const status = Number(details?.status);
    const isBankTransfer =
      paymentType === PaymentType.BANK_TRANSFER ||
      paymentType === PaymentType.BR_BANK_TRANSFER;
    const isAwaitingPayment = status === 1 || status === 2;

    if (!isBankTransfer || !isAwaitingPayment) {
      // Also clear bankTransferLoading here. Without it, the sequence
      // "BT order loading → user navigates to non-BT order → BT response
      // arrives stale" leaves the spinner stuck: the in-flight fetch's
      // finally only clears loading when the orderId still matches, and
      // it no longer does, so nothing else turns it off.
      this.bankTransferConfig.set(null);
      this.bankTransferError.set(false);
      this.bankTransferOrderId.set(null);
      this.bankTransferLoading.set(false);
      return;
    }

    this.bankTransferOrderId.set(Number(details.orderId || details.id));
    await this.fetchBankTransferConfig();
  }

  /**
   * Performs the actual GET /api/payments/bank-transfer/orders/:id/config
   * fetch. Split out from maybeLoadBankTransferConfig so the template's
   * retry button can re-trigger it without re-evaluating the gate.
   */
  async fetchBankTransferConfig(): Promise<void> {
    const orderId = this.bankTransferOrderId();
    if (!orderId) return;

    this.bankTransferLoading.set(true);
    this.bankTransferError.set(false);
    try {
      const cfg = await firstValueFrom(this.paymentService.getBankTransferConfig(orderId));
      // Discard stale responses. If the user paged to a different order
      // while this request was in flight, bankTransferOrderId no longer
      // matches — writing cfg here would render order N's IBAN onto
      // order M's panel (cross-order coordinate contamination).
      if (this.bankTransferOrderId() !== orderId) return;
      this.bankTransferConfig.set(cfg);
    } catch (err) {
      console.warn('[OrderDetailsDialog] Failed to fetch bank-transfer config:', err);
      if (this.bankTransferOrderId() !== orderId) return;
      this.bankTransferConfig.set(null);
      this.bankTransferError.set(true);
    } finally {
      // Only clear the loading flag if we're still on the same order.
      if (this.bankTransferOrderId() === orderId) {
        this.bankTransferLoading.set(false);
      }
    }
  }

  // ========== Multibanco Methods ==========

  /**
   * Fetch Multibanco entity/reference/amount when the order is awaiting
   * Multibanco payment. Same gate shape as the bank-transfer path; the
   * email link from the order confirmation lands here and the customer
   * must be able to recover the reference.
   */
  private async maybeLoadMultibancoConfig(details: any): Promise<void> {
    const paymentType = Number(details?.payment?.type);
    const status = Number(details?.status);
    const isMultibanco = paymentType === PaymentType.MULTIBANCO_V2;
    const isAwaitingPayment = status === 1 || status === 2;

    if (!isMultibanco || !isAwaitingPayment) {
      // Mirror the bank-transfer path: clear loading too so an in-flight
      // response that arrives after the user navigated away can't leave
      // the spinner stuck.
      this.multibancoConfig.set(null);
      this.multibancoError.set(false);
      this.multibancoOrderId.set(null);
      this.multibancoLoading.set(false);
      return;
    }

    this.multibancoOrderId.set(Number(details.orderId || details.id));
    await this.fetchMultibancoConfig();
  }

  /**
   * Split out from maybeLoadMultibancoConfig so the template can re-trigger
   * it. Snapshots the orderId before the await and bails out on a stale
   * response to prevent cross-order data contamination.
   */
  async fetchMultibancoConfig(): Promise<void> {
    const orderId = this.multibancoOrderId();
    if (!orderId) return;

    this.multibancoLoading.set(true);
    this.multibancoError.set(false);
    try {
      const cfg = await firstValueFrom(this.paymentService.getMultibancoConfig(orderId));
      if (this.multibancoOrderId() !== orderId) return;
      this.multibancoConfig.set(cfg);
    } catch (err) {
      console.warn('[OrderDetailsDialog] Failed to fetch multibanco config:', err);
      if (this.multibancoOrderId() !== orderId) return;
      this.multibancoConfig.set(null);
      this.multibancoError.set(true);
    } finally {
      if (this.multibancoOrderId() === orderId) {
        this.multibancoLoading.set(false);
      }
    }
  }

  // ========== Viva Wallet (popup checkout + postMessage) ==========
  //
  // Mirrors dreambooks-web's flow: the order code is PREFETCHED while the
  // order awaits payment (idempotent GET — repeat calls return the same
  // active code), the Smart Checkout opens in a POPUP from the click
  // handler (synchronous window.open — no blocker risk, and the app stays
  // on the order), and the dashboard-registered return URLs land on our
  // /vivapayments/success|failure page, which postMessages {status,
  // transactionId} back here and closes itself. On success the backend
  // confirmation endpoint verifies the transaction — the redirect params
  // are never trusted as the result.

  vivaOrderCode = signal<string | null>(null);
  vivaLoading = signal(false);
  vivaOrderId = signal<number | null>(null);
  vivaError = signal(false);
  vivaConfirming = signal(false);
  // Set while the dead code is being retired + a fresh one minted after a
  // failed checkout (the abort→re-fetch round-trip).
  vivaAborting = signal(false);
  // The order is validating(2)/paid after the abort said "not awaiting
  // payment" — show a processing note and do NOT offer retry (double-charge
  // risk per the Viva abort contract).
  vivaProcessing = signal(false);
  // The current code-ready state is a retry after a prior failure — switches
  // the hint to "previous attempt failed, try again".
  vivaRetry = signal(false);
  private vivaWindow: WindowProxy | null = null;
  private vivaChannel: BroadcastChannel | null = null;

  // window.opener fallback path (older browsers). Primary path is the
  // BroadcastChannel set up in openVivaCheckout.
  private handleVivaMessage = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin) {
      console.log('[Viva] 5. message IGNORED — foreign origin:', event.origin);
      return;
    }
    if (event.data?.source !== 'viva-return') return;
    console.log('[Viva] 5. relay received via postMessage:', event.data);
    this.handleVivaResult(event.data.status, event.data.transactionId);
  };

  private handleVivaResult(status: string, transactionId: string | null): void {
    // Tear down both relay channels + the popup reference (idempotent — the
    // first of postMessage/BroadcastChannel to arrive wins).
    window.removeEventListener('message', this.handleVivaMessage);
    this.vivaChannel?.close();
    this.vivaChannel = null;
    try {
      this.vivaWindow?.close();
    } catch { /* already closed */ }
    this.vivaWindow = null;

    if (status === 'success' && transactionId) {
      console.log('[Viva] 6. success, transactionId=', transactionId, '→ confirming');
      void this.confirmVivaPayment(String(transactionId));
    } else {
      console.log('[Viva] 6. status=', status, '→ FAILURE: aborting dead code, minting fresh');
      void this.retryVivaAfterFailure();
    }
  }

  /**
   * Failure/cancel handling: retire the consumed order code via the abort
   * endpoint, then mint a fresh one so the next attempt isn't instant-
   * redirected to failure (Viva codes go terminal after a decline). Also
   * the manual "Tentar Novamente" handler. Per the abort contract:
   *  - aborted / no-active → re-fetch a fresh code (pay button reappears)
   *  - not awaiting payment → validating(2)/paid: show processing, reload,
   *    do NOT retry (double-charge risk)
   */
  async retryVivaAfterFailure(): Promise<void> {
    const orderId = this.vivaOrderId() || Number((this.orderDetails() as any)?.orderId || (this.orderDetails() as any)?.id);
    if (!orderId) return;
    this.vivaOrderId.set(orderId);

    this.vivaOrderCode.set(null);
    this.vivaError.set(false);
    this.vivaProcessing.set(false);
    this.vivaAborting.set(true);
    try {
      console.log('[Viva] 6a. POST /payments/viva/orders/' + orderId + '/abort …');
      const res = await firstValueFrom(this.paymentService.abortViva(orderId));
      const msg = res?.message || '';
      console.log('[Viva] 6a. abort →', msg);

      if (msg.startsWith('Payment aborted') || msg.startsWith('No active')) {
        // Dead code retired (or none) → mint a fresh one; pay button returns.
        this.vivaRetry.set(true);
        await this.fetchVivaConfig();
      } else {
        // "Order is not awaiting payment" → validating/paid. Don't retry.
        console.log('[Viva] 6a. order not awaiting payment → processing, reloading');
        this.vivaProcessing.set(true);
        this.loadOrderDetails();
      }
    } catch (err) {
      console.warn('[Viva] 6a. abort failed:', err);
      this.vivaError.set(true);
    } finally {
      this.vivaAborting.set(false);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleVivaMessage);
    this.vivaChannel?.close();
    this.vivaChannel = null;
  }

  private async maybeLoadVivaConfig(details: any): Promise<void> {
    const paymentType = Number(details?.payment?.type);
    const status = Number(details?.status);
    const isViva = paymentType === PaymentType.VIVA_WALLET;
    // Viva is payable only at status 1 (pending). status 2 = the gateway is
    // validating a payment in flight — offering the button there risks a
    // second charge (per the abort contract), so show no button.
    const isPayable = status === 1;

    if (!isViva || !isPayable) {
      if (isViva) {
        console.log('[Viva] 1. not payable (status=', status, ')');
      }
      this.vivaOrderCode.set(null);
      this.vivaError.set(false);
      this.vivaOrderId.set(null);
      this.vivaLoading.set(false);
      this.vivaRetry.set(false);
      // Clear the processing note once the order leaves validation
      // (paid/cancelled); keep it while still validating (status 2).
      if (status !== 2) this.vivaProcessing.set(false);
      return;
    }

    const orderId = Number(details.orderId || details.id);
    console.log('[Viva] 1. payable Viva order (status 1), prefetching code for', orderId);
    this.vivaOrderId.set(orderId);
    this.vivaProcessing.set(false);
    this.vivaRetry.set(false);
    await this.fetchVivaConfig();
  }

  async fetchVivaConfig(): Promise<void> {
    const orderId = this.vivaOrderId();
    if (!orderId) return;

    console.log('[Viva] 2. GET /payments/viva/orders/' + orderId + ' …');
    this.vivaLoading.set(true);
    this.vivaError.set(false);
    try {
      const config = await firstValueFrom(this.paymentService.getVivaOrderCode(orderId));
      if (this.vivaOrderId() !== orderId) {
        console.log('[Viva] 2. stale response for order', orderId, '— ignored');
        return;
      }
      console.log('[Viva] 2. order code received:', config.orderCode);
      this.vivaOrderCode.set(config.orderCode);
    } catch (err) {
      console.warn('[Viva] 2. GET order code FAILED:', err);
      if (this.vivaOrderId() !== orderId) return;
      this.vivaOrderCode.set(null);
      this.vivaError.set(true);
    } finally {
      if (this.vivaOrderId() === orderId) {
        this.vivaLoading.set(false);
      }
    }
  }

  openVivaCheckout(): void {
    const orderCode = this.vivaOrderCode();
    if (!orderCode) {
      console.warn('[Viva] 3. openVivaCheckout called with no order code — aborting');
      return;
    }

    const url = `${environment.vivaUrl}?ref=${orderCode}&color=${environment.vivaColorCode}`;
    console.log('[Viva] 3. opening checkout popup:', url);
    if (!this.vivaWindow || this.vivaWindow.closed) {
      this.vivaWindow = window.open(url, '_blank', 'width=900,height=800');
    }
    console.log('[Viva] 3. popup window =', this.vivaWindow ? 'opened' : 'BLOCKED (null)');
    this.vivaWindow?.focus();

    // Primary relay channel: BroadcastChannel (same-origin, survives Chrome
    // severing window.opener on the cross-origin Viva navigation). The
    // window 'message' listener is a fallback for older browsers.
    this.vivaChannel?.close();
    try {
      this.vivaChannel = new BroadcastChannel('viva-payment');
      this.vivaChannel.onmessage = (ev: MessageEvent) => {
        if (ev.data?.source !== 'viva-return') return;
        console.log('[Viva] 5. relay received via BroadcastChannel:', ev.data);
        this.handleVivaResult(ev.data.status, ev.data.transactionId);
      };
    } catch (e) {
      console.warn('[Viva] BroadcastChannel unavailable, relying on postMessage:', e);
    }
    window.removeEventListener('message', this.handleVivaMessage);
    window.addEventListener('message', this.handleVivaMessage);
    console.log('[Viva] 4. listening for relay (BroadcastChannel + postMessage) …');
  }

  /**
   * Fast-path confirmation after the popup reports success: the backend
   * re-fetches the transaction from Viva and verifies status, order
   * binding and exact amount. A 422 is NOT a failure — the webhook may
   * still confirm — so every outcome ends in a reload, which shows the
   * order's real status.
   */
  private async confirmVivaPayment(transactionId: string): Promise<void> {
    const orderId = this.vivaOrderId() || Number((this.orderDetails() as any)?.orderId || (this.orderDetails() as any)?.id);
    if (!orderId) return;

    console.log('[Viva] 7. GET /payments/viva/confirmation/' + orderId + '/' + transactionId + ' …');
    this.vivaConfirming.set(true);
    try {
      const res = await firstValueFrom(this.paymentService.confirmViva(orderId, transactionId));
      console.log('[Viva] 7. confirmation OK:', res);
    } catch (err) {
      console.warn('[Viva] 7. confirmation returned error (webhook may still confirm):', err);
    } finally {
      this.vivaConfirming.set(false);
      console.log('[Viva] 8. reloading order to reflect real status');
      this.loadOrderDetails();
    }
  }

  // ========== Cielo (new-tab checkout) ==========
  //
  // The checkout URL is PREFETCHED while the order awaits payment (the
  // config GET is idempotent per contract — repeat calls return the same
  // URL until a failed transaction invalidates it). That lets the button
  // be a plain target=_blank anchor: popup blockers never interfere (a
  // window.open after an awaited fetch would be blocked), and the app tab
  // stays on the order, so the customer can't get lost even if Cielo's
  // return redirect is dropped (the backend rejects return_url origins
  // that don't match the configured app_url — e.g. localhost in dev).

  cieloUrl = signal<string | null>(null);
  cieloLoading = signal(false);
  cieloOrderId = signal<number | null>(null);
  cieloError = signal(false);

  private async maybeLoadCieloConfig(details: any): Promise<void> {
    const paymentType = Number(details?.payment?.type);
    const status = Number(details?.status);
    const isCielo = paymentType === PaymentType.CIELO;
    const isAwaitingPayment = status === 1 || status === 2;

    if (!isCielo || !isAwaitingPayment) {
      this.cieloUrl.set(null);
      this.cieloError.set(false);
      this.cieloOrderId.set(null);
      this.cieloLoading.set(false);
      return;
    }

    this.cieloOrderId.set(Number(details.orderId || details.id));
    await this.fetchCieloConfig();
  }

  async fetchCieloConfig(): Promise<void> {
    const orderId = this.cieloOrderId();
    if (!orderId) return;

    this.cieloLoading.set(true);
    this.cieloError.set(false);
    try {
      // Return leg: payment-result polls the order status (the redirect
      // carries no outcome params; confirmation is webhook-driven).
      const returnUrl = `${window.location.origin}/${this.appState.getLocale()}/payment-result?gateway=cielo&orderId=${orderId}`;
      const config = await firstValueFrom(this.paymentService.getCieloConfig(orderId, returnUrl));
      if (this.cieloOrderId() !== orderId) return;
      this.cieloUrl.set(config.url);
    } catch (err) {
      console.warn('[OrderDetailsDialog] Failed to fetch cielo config:', err);
      if (this.cieloOrderId() !== orderId) return;
      this.cieloUrl.set(null);
      this.cieloError.set(true);
    } finally {
      if (this.cieloOrderId() === orderId) {
        this.cieloLoading.set(false);
      }
    }
  }

  /**
   * Called when the customer opens the Cielo tab: refresh the order the
   * next time this tab regains focus, so a completed payment is reflected
   * without a manual reload (webhook permitting).
   */
  onCieloOpened(): void {
    const handler = () => {
      window.removeEventListener('focus', handler);
      this.loadOrderDetails();
    };
    window.addEventListener('focus', handler);
  }

  // ========== Payment Initiation (checkout handoff) ==========

  /**
   * One-shot payment initiation handed off from checkout
   * (data.initiatePaymentTypeId). Only fires while the order is still
   * pending (status 1). A failure is logged and left to the per-gateway
   * recovery surfaces — the boleto/multibanco config GETs lazily
   * (re)generate coordinates, PayPal/Braintree initiate on interaction —
   * and the order is already placed, so the user has nothing to redo
   * (and can no longer accidentally place it twice from the cart).
   */
  private async maybeInitiatePayment(details: any): Promise<any> {
    const typeId = Number(this.data.initiatePaymentTypeId) || 0;
    if (!typeId) return details;
    this.data.initiatePaymentTypeId = null; // one-shot — reloads/navigation never re-fire

    if (Number(details?.status) !== 1) return details; // already initiated (or paid)
    const orderId = Number(details.orderId || details.id);
    if (!orderId) return details;

    try {
      const pr = await firstValueFrom(this.paymentService.processPayment(orderId, typeId));
      if (pr?.success !== false && pr?.status !== 'error') {
        // Re-fetch the order so the badge reflects the AUTHORITATIVE server
        // status rather than an optimistic guess. The gateway's response
        // status is NOT the order status: e.g. the customer-account type
        // reports 'approved' (balance accepted) while the order itself stays
        // at "awaiting payment" (status 1) through a confirmation window.
        // Synthesizing status=3 here showed "Payment confirmed" until the
        // next reload surfaced the real pending state — confusing the user.
        const refreshed = await firstValueFrom(this.orderService.getOrderDetails(this.data.order));
        if (refreshed) return refreshed;
      } else {
        console.warn('[OrderDetailsDialog] processPayment returned error status:', pr);
      }
    } catch (err) {
      console.warn('[OrderDetailsDialog] processPayment failed — per-gateway recovery applies:', err);
    }
    return details;
  }

  // ========== Boleto Methods ==========

  /**
   * Fetch the Bradesco boleto (URL + linha digitável) when the order is
   * awaiting a BOLETO_V2 payment. Same gate shape as multibanco. The GET
   * is idempotent server-side — it returns the already-generated boleto
   * (or generates it if the placement call failed mid-way), so re-entering
   * the order page always recovers the document.
   */
  private async maybeLoadBoletoConfig(details: any): Promise<void> {
    const paymentType = Number(details?.payment?.type);
    const status = Number(details?.status);
    const isBoleto = paymentType === PaymentType.BOLETO_V2;
    const isAwaitingPayment = status === 1 || status === 2;

    if (!isBoleto || !isAwaitingPayment) {
      this.boletoConfig.set(null);
      this.boletoError.set(false);
      this.boletoOrderId.set(null);
      this.boletoLoading.set(false);
      return;
    }

    this.boletoOrderId.set(Number(details.orderId || details.id));
    await this.fetchBoletoConfig();
  }

  /**
   * Split out from maybeLoadBoletoConfig so the template can re-trigger it.
   * Snapshots the orderId before the await and bails out on a stale
   * response to prevent cross-order data contamination.
   */
  async fetchBoletoConfig(): Promise<void> {
    const orderId = this.boletoOrderId();
    if (!orderId) return;

    this.boletoLoading.set(true);
    this.boletoError.set(false);
    try {
      const cfg = await firstValueFrom(this.paymentService.getBoletoConfig(orderId));
      if (this.boletoOrderId() !== orderId) return;
      this.boletoConfig.set(cfg);
    } catch (err) {
      console.warn('[OrderDetailsDialog] Failed to fetch boleto config:', err);
      if (this.boletoOrderId() !== orderId) return;
      this.boletoConfig.set(null);
      this.boletoError.set(true);
    } finally {
      if (this.boletoOrderId() === orderId) {
        this.boletoLoading.set(false);
      }
    }
  }

  // ========== Project Thumbnails Methods ==========

  /**
   * Load project thumbnails for each group that has a draftId.
   * The draft still exists after ordering (ordered=1), so we can load it
   * and extract the same thumbnails shown in draft-details-dialog.
   */
  private async loadProjectThumbnails(details: any): Promise<void> {
    if (!details?.groups || details.groups.length === 0) return;

    const newMap = new Map<number, {
      groupName: string;
      thumbnails: { label: string; dataUrl: string }[];
      details?: { label: string; value: string }[];
    }[]>();
    const coverMap = new Map<number, string>();
    this.vectorThumbByKey.clear();

    for (let i = 0; i < details.groups.length; i++) {
      const group = details.groups[i];
      const draftId = group.draftId || group.draft_order_id;

      if (!draftId || draftId <= 0) continue;

      try {
        console.log(`[OrderDetailsDialog] Loading draft ${draftId} for group ${i}`);
        const draftData = await firstValueFrom(this.draftService.loadDraft({ id: draftId } as any));

        if (draftData) {
          // Extract cover thumbnail
          if (draftData.draftThumbnail) {
            coverMap.set(i, draftData.draftThumbnail);
          }

          const thumbGroups = this.collectProjectThumbnails(draftData);
          if (thumbGroups.length > 0) {
            newMap.set(i, thumbGroups);
          }

          // The order endpoint's product.files carry no thumbUrl — the
          // persisted finish-file render thumbs live in this draft JSON.
          this.harvestVectorThumbs(draftData);
        }
      } catch (error) {
        console.warn(`[OrderDetailsDialog] Could not load draft ${draftId} for group ${i}:`, error);
      }
    }

    this.groupThumbnails.set(newMap);
    this.groupCoverThumbnails.set(coverMap);
  }

  /**
   * Index every finish-file render thumb found anywhere in the draft JSON
   * (finishFiles live under several shapes: productConfig.finishFiles,
   * albumConfig.cover/panels[].selectedProduct.finishFiles, …) so
   * getVectorThumb can resolve previews for the order's vector files by
   * path/serverName/name. A structural walk keeps this immune to shape
   * differences between product types.
   */
  private harvestVectorThumbs(data: any): void {
    let found = 0;
    const walk = (node: any, depth: number): void => {
      if (!node || depth > 12) return;
      if (Array.isArray(node)) {
        for (const item of node) walk(item, depth + 1);
        return;
      }
      if (typeof node !== 'object') return;
      const t = node.thumbUrl || node.thumb_url;
      if (typeof t === 'string' && (t.startsWith('data:image') || /^https?:\/\//i.test(t))) {
        for (const key of [node.path, node.serverName, node.server_name, node.name]) {
          if (typeof key === 'string' && key && !this.vectorThumbByKey.has(key)) {
            this.vectorThumbByKey.set(key, t);
            found++;
          }
        }
      }
      for (const k of Object.keys(node)) {
        const v = (node as any)[k];
        if (v && typeof v === 'object') walk(v, depth + 1);
      }
    };
    walk(data, 0);
    if (found > 0) {
      console.log('[OrderDetailsDialog] Harvested', found, 'file thumb key(s) from the draft JSON');
    }
  }

  /**
   * Collect project thumbnails (base64 page renders) from DraftData.
   * Same logic as draft-details-dialog.component.ts collectProjectThumbnails().
   */
  private collectProjectThumbnails(data: DraftData): {
    groupName: string;
    thumbnails: { label: string; dataUrl: string }[];
    details?: { label: string; value: string }[];
  }[] {
    const groups: {
      groupName: string;
      thumbnails: { label: string; dataUrl: string }[];
      details?: { label: string; value: string }[];
    }[] = [];
    const pageLabel = this.translate.instant('ORDERS.DRAFTS.PAGE');
    // Album projects label their pages "Folha N" everywhere else (editor
    // strip, products form) — reuse the editor's key so labels can't drift.
    const sheetLabel = (i: number) => this.translate.instant('EDITOR.SHEET_N', { n: i + 1 });

    if (data.isAlbum && data.albumConfig) {
      const albumConfig = data.albumConfig;

      // Album page thumbnails
      if (albumConfig.albumPageThumbnails && albumConfig.albumPageThumbnails.length > 0) {
        groups.push({
          groupName: this.translate.instant('ORDERS.DRAFTS.ALBUM_PAGES'),
          thumbnails: albumConfig.albumPageThumbnails.map((dataUrl, i) => ({
            label: sheetLabel(i),
            dataUrl
          }))
        });
      }

      // Cover project thumbnail
      if (albumConfig.coverProjectTransparentThumbnail) {
        groups.push({
          groupName: this.translate.instant('DRAFTS.COVER'),
          thumbnails: [{ label: this.translate.instant('DRAFTS.COVER'), dataUrl: albumConfig.coverProjectTransparentThumbnail }]
        });
      }

      // Album panel project thumbnails
      if (albumConfig.panels && Array.isArray(albumConfig.panels)) {
        albumConfig.panels.forEach(panel => {
          if (!panel.isOptedOut && panel.projectThumbnails && panel.projectThumbnails.length > 0) {
            groups.push({
              groupName: panel.selectedProduct?.productName || panel.panelName,
              thumbnails: panel.projectThumbnails.map((dataUrl, i) => ({
                label: sheetLabel(i),
                dataUrl
              }))
            });
          }
        });
      }

      // Cover panel project thumbnails
      if (albumConfig.cover?.panels && Array.isArray(albumConfig.cover.panels)) {
        albumConfig.cover.panels.forEach(panel => {
          if (!panel.isOptedOut && panel.projectThumbnails && panel.projectThumbnails.length > 0) {
            groups.push({
              groupName: panel.selectedProduct?.productName || panel.panelName,
              thumbnails: panel.projectThumbnails.map((dataUrl, i) => ({
                label: sheetLabel(i),
                dataUrl
              }))
            });
          }
        });
      }
    } else if (data.productConfig) {
      const productConfig = data.productConfig;

      // Main product project thumbnails. A multi-design draft (several editor
      // projects of the product — one order line each) gets one group per
      // design with its thumbnails and what it changes vs the common finishes.
      const designs = productConfig.projects ?? [];
      if (designs.length > 1) {
        const baseNames = productConfig.finishNames ?? {};
        designs.forEach((ref, index) => {
          const details: { label: string; value: string }[] = [];
          const names = ref.finishNames ?? {};
          Object.keys(ref.finishValues ?? {})
            .map(key => Number(key))
            .sort((a, b) => (names[a]?.order ?? baseNames[a]?.order ?? a) - (names[b]?.order ?? baseNames[b]?.order ?? b))
            .forEach(optionId => {
              const own = names[optionId];
              const base = baseNames[optionId];
              if (!own || (base && base.valueName === own.valueName)) return;
              details.push({ label: own.optionName, value: own.valueName });
            });
          if (details.length === 0) {
            details.push({ label: this.translate.instant('PRODUCTS.PROJECT_USES_COMMON'), value: '' });
          }
          groups.push({
            groupName: `${this.translate.instant('PRODUCTS.PROJECT_SAVED')} ${index + 1}`,
            thumbnails: (ref.thumbnails ?? []).map((dataUrl, i) => ({
              label: `${pageLabel} ${i + 1}`,
              dataUrl
            })),
            details
          });
        });
      } else if (productConfig.projectThumbnails && productConfig.projectThumbnails.length > 0) {
        groups.push({
          groupName: productConfig.productName || this.translate.instant('COMMON.PRODUCT'),
          thumbnails: productConfig.projectThumbnails.map((dataUrl, i) => ({
            label: `${pageLabel} ${i + 1}`,
            dataUrl
          }))
        });
      }

      // Product panel project thumbnails
      if (productConfig.panels && Array.isArray(productConfig.panels)) {
        productConfig.panels.forEach(panel => {
          if (!panel.isOptedOut && panel.projectThumbnails && panel.projectThumbnails.length > 0) {
            groups.push({
              groupName: panel.selectedProduct?.productName || panel.panelName,
              thumbnails: panel.projectThumbnails.map((dataUrl, i) => ({
                label: `${pageLabel} ${i + 1}`,
                dataUrl
              }))
            });
          }
        });
      }
    }

    return groups;
  }

  /**
   * Get thumbnail groups for a specific order group index
   */
  getGroupThumbnails(groupIdx: number): {
    groupName: string;
    thumbnails: { label: string; dataUrl: string }[];
    details?: { label: string; value: string }[];
  }[] {
    return this.groupThumbnails().get(groupIdx) || [];
  }

  getGroupCoverThumbnail(groupIdx: number): string | null {
    return this.groupCoverThumbnails().get(groupIdx) || null;
  }

  /**
   * Open the image viewer for project thumbnails
   */
  openThumbnailViewer(groupIdx: number, thumbGroupIdx: number, thumbIdx: number): void {
    const thumbGroups = this.getGroupThumbnails(groupIdx);
    if (!thumbGroups[thumbGroupIdx]) return;

    const group = thumbGroups[thumbGroupIdx];
    const viewerFiles = group.thumbnails.map(thumb => ({
      name: thumb.label,
      thumbUrl: thumb.dataUrl
    }));

    this.viewerImages.set(viewerFiles);
    this.currentImageIndex.set(thumbIdx);
    this.imageViewerOpen.set(true);
    document.body.style.overflow = 'hidden';
  }

  close(): void {
    this.dialogRef.close();
  }
}
